import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  OFFER_BANNER_THEME,
  OFFER_SERVICE_GENERAL,
  OFFER_SERVICE_LABEL,
  offerBadgeLabel,
  type GuestOffer,
  type OfferService,
} from '@hotel-voice/shared';
import { useStorageUrl } from '@/hooks/useStorageUrl';
import { cn } from '@/lib/utils';

const ADVANCE_MS = 4000;

/** Where a banner sends the guest. Hotel-wide offers go nowhere. */
const SERVICE_ROUTE: Partial<Record<OfferService, string>> = {
  restaurant: '/restaurant',
  store: '/store',
  laundry: '/laundry',
  housekeeping: '/housekeeping',
  wifi: '/wifi',
};

function OfferBanner({ offer }: { offer: GuestOffer }) {
  const theme = OFFER_BANNER_THEME[offer.service] ?? OFFER_BANNER_THEME.general;
  const imageUrl = useStorageUrl(offer.image?.path);
  const to = offer.service === OFFER_SERVICE_GENERAL ? undefined : SERVICE_ROUTE[offer.service];

  const body = (
    <>
      {imageUrl ? (
        <>
          <img src={imageUrl} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        </>
      ) : (
        <>
          <div
            className="absolute -top-16 -right-12 size-56 rounded-full opacity-35"
            style={{ background: theme.to }}
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-14 right-20 size-32 rounded-full opacity-25"
            style={{ background: theme.to }}
            aria-hidden="true"
          />
        </>
      )}
      <div className="relative flex h-full flex-col justify-between p-4 text-white">
        <div className="flex items-center justify-between gap-2">
          <span className="max-w-[60%] truncate rounded-full bg-white px-3 py-1 text-[11px] font-bold tracking-wide text-neutral-900">
            {offerBadgeLabel(offer)}
          </span>
          <span className="truncate text-[11px] font-semibold tracking-wider uppercase text-white/85">
            {OFFER_SERVICE_LABEL[offer.service]}
          </span>
        </div>
        <div>
          <p className="truncate text-xl font-bold leading-tight">{offer.name}</p>
          <p className="mt-0.5 line-clamp-2 text-sm text-white/90">{offer.info}</p>
        </div>
      </div>
    </>
  );

  const className = 'relative block aspect-[2/1] w-full overflow-hidden rounded-2xl shadow-sm';
  // Without an image the card is a gradient of the service's two colours; with
  // one, the same `from` sits under the photo while it loads.
  const style = {
    background: imageUrl
      ? theme.from
      : `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`,
  };

  return to ? (
    <Link
      to={to}
      className={cn(className, 'transition-transform active:scale-[0.98] focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none')}
      style={style}
      aria-label={`${offer.name}: ${offer.info}`}
    >
      {body}
    </Link>
  ) : (
    <div className={className} style={style} role="group" aria-label={offer.name}>
      {body}
    </div>
  );
}

/**
 * The offers carousel on the home page: one banner per screen, swipe to move,
 * dots below, advancing on its own every few seconds. Scroll-snap does the
 * paging so a swipe always lands on a whole card, and the timer pauses while
 * a finger is down or the tab is hidden. Users who asked their OS for reduced
 * motion get no auto-advance at all — the dots and swipe still work.
 */
export function OfferCarousel({ offers, className }: { offers: GuestOffer[]; className?: string }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = offers.length;
  const current = Math.min(index, Math.max(count - 1, 0));

  const scrollTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    if (paused || count < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      const next = (current + 1) % count;
      scrollTo(next);
      setIndex(next);
    }, ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [paused, count, current]);

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  if (count === 0) return null;

  return (
    <section aria-label="Offers" className={className}>
      <div
        ref={trackRef}
        onScroll={handleScroll}
        onPointerDown={() => setPaused(true)}
        onPointerUp={() => setPaused(false)}
        onPointerCancel={() => setPaused(false)}
        className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {offers.map((offer) => (
          <div key={offer.id} className="w-full shrink-0 snap-center">
            <OfferBanner offer={offer} />
          </div>
        ))}
      </div>
      {count > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1.5" role="tablist" aria-label="Offer pages">
          {offers.map((offer, i) => (
            <button
              key={offer.id}
              type="button"
              role="tab"
              aria-selected={i === current}
              aria-label={`Offer ${i + 1} of ${count}`}
              onClick={() => {
                scrollTo(i);
                setIndex(i);
              }}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === current ? 'bg-primary w-4' : 'bg-muted-foreground/40 w-1.5',
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}
